/**
 * NodePalette — Draggable sidebar palette for the Visual Agent Builder
 *
 * Users drag items from here onto the React Flow canvas to create nodes.
 * Categories: Data Sources, Tools, Outputs, Logic
 */

import React, { useState } from 'react';
import {
  Database, Wrench, FileText, GitBranch, Brain,
  ChevronDown, ChevronRight, Search, Lock,
  Target, Radio, Eye, Video, Watch, Clipboard, DollarSign,
  Users, BarChart3, Scissors, Calculator, Monitor,
  MessageSquare, Mail, FileDown, Globe, BookOpen, Upload,
  Circle, Grid, Heart, Plug, Film, Smartphone, Image,
} from 'lucide-react';
import { DATA_SOURCES, AGENT_TOOLS, OUTPUT_CHANNELS, PILLAR_CONFIG, groupByPillar } from '../canvasConfig';
import { NODE_COLORS } from './FlowNodes';

const ICON_MAP = {
  Target, Radio, Eye, Video, Watch, Clipboard, DollarSign,
  Users, BarChart3, Scissors, Calculator, Monitor, Database,
  MessageSquare, Mail, FileDown, Globe, BookOpen, Upload,
  Circle, Grid, Heart, Plug, Film, Smartphone, Image, Brain,
  Wrench, FileText, GitBranch, Search, Lock,
};

function getIcon(name) {
  return ICON_MAP[name] || Circle;
}

// Build pillar-grouped categories
const ALL_ITEMS = [
  ...DATA_SOURCES.filter(s => s.available).map(s => ({ ...s, _nodeType: 'dataSource' })),
  ...AGENT_TOOLS.filter(t => t.available).map(t => ({ ...t, _nodeType: 'tool' })),
  ...OUTPUT_CHANNELS.filter(o => o.available).map(o => ({ ...o, _nodeType: 'output' })),
];

const PILLAR_CATEGORIES = Object.entries(PILLAR_CONFIG)
  .filter(([id]) => id !== 'utility')
  .map(([id, cfg]) => {
    const grouped = groupByPillar(ALL_ITEMS);
    return {
      id,
      label: cfg.label,
      icon: ICON_MAP[cfg.icon] || Circle,
      color: { accent: cfg.color, bg: cfg.colorDim },
      items: grouped[id] || [],
    };
  })
  .filter(cat => cat.items.length > 0);

// Utility + Logic always at bottom
const UTILITY_ITEMS = groupByPillar(ALL_ITEMS).utility || [];
const LOGIC_ITEMS = [
  { id: 'condition', name: 'Condition', icon: 'GitBranch', desc: 'Branch based on criteria', _nodeType: 'condition' },
  { id: 'agent', name: 'AI Agent', icon: 'Brain', desc: 'Add another AI agent', _nodeType: 'agent' },
];

const CATEGORIES = [
  ...PILLAR_CATEGORIES,
  ...(UTILITY_ITEMS.length > 0 ? [{
    id: 'utility',
    label: 'Utility',
    icon: Wrench,
    color: { accent: '#999', bg: '#99999930' },
    items: UTILITY_ITEMS,
  }] : []),
  {
    id: 'logic',
    label: 'Logic',
    icon: GitBranch,
    color: NODE_COLORS.condition,
    items: LOGIC_ITEMS,
  },
];

function PaletteItem({ item, color, onDragStart }) {
  const Icon = getIcon(item.icon);
  const actualType = item._nodeType || 'tool';

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('application/reactflow-type', actualType);
        e.dataTransfer.setData('application/reactflow-data', JSON.stringify({
          label: item.name,
          description: item.desc || '',
          icon: item.icon,
          category: item.category || '',
          toolId: item.id,
          available: item.available !== false,
        }));
        e.dataTransfer.effectAllowed = 'move';
        onDragStart?.();
      }}
      className="flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-grab active:cursor-grabbing transition-all hover:scale-[1.02] group"
      style={{
        background: '#0A0A0A',
        border: `1px solid #1A1A1A`,
      }}
      title={item.desc}
    >
      <div
        className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
        style={{ background: color.accent + '15' }}
      >
        <Icon size={12} style={{ color: color.accent }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-medium truncate" style={{ color: '#F5F5F5' }}>
          {item.name}
        </p>
      </div>
    </div>
  );
}

function PaletteCategory({ category, onDragStart }) {
  const [expanded, setExpanded] = useState(category.id === 'tools');
  const Icon = category.icon;
  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <div className="mb-1">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-2 px-2 py-2 rounded-lg transition-colors hover:bg-white/[0.03]"
      >
        <Icon size={14} style={{ color: category.color.accent }} />
        <span className="text-[11px] font-semibold uppercase tracking-wider flex-1 text-left" style={{ color: '#999' }}>
          {category.label}
        </span>
        <span
          className="text-[9px] px-1.5 py-0.5 rounded-full"
          style={{ background: category.color.accent + '15', color: category.color.accent }}
        >
          {category.items.length}
        </span>
        <Chevron size={12} style={{ color: '#555' }} />
      </button>

      {expanded && (
        <div className="space-y-1 mt-1 ml-1">
          {category.items.map(item => (
            <PaletteItem
              key={item.id}
              item={item}
              color={category.color}
              onDragStart={onDragStart}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function NodePalette({ onDragStart, searchQuery, onSearchChange }) {
  const [localSearch, setLocalSearch] = useState('');
  const query = searchQuery ?? localSearch;
  const setQuery = onSearchChange ?? setLocalSearch;

  const filteredCategories = CATEGORIES.map(cat => ({
    ...cat,
    items: query
      ? cat.items.filter(i =>
          i.name.toLowerCase().includes(query.toLowerCase()) ||
          (i.desc || '').toLowerCase().includes(query.toLowerCase())
        )
      : cat.items,
  })).filter(cat => cat.items.length > 0);

  return (
    <div
      className="w-[220px] flex-shrink-0 flex flex-col h-full overflow-hidden rounded-xl"
      style={{ background: '#050505', border: '1px solid #1A1A1A' }}
      data-tour="node-palette"
    >
      {/* Header */}
      <div className="px-3 py-3 border-b" style={{ borderColor: '#1A1A1A' }}>
        <p className="text-[10px] font-semibold uppercase tracking-widest mb-2" style={{ color: '#555' }}>
          Node Palette
        </p>
        <div
          className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg"
          style={{ background: '#0A0A0A', border: '1px solid #1A1A1A' }}
        >
          <Search size={12} style={{ color: '#555' }} />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search nodes..."
            className="flex-1 bg-transparent text-[11px] outline-none"
            style={{ color: '#F5F5F5' }}
          />
        </div>
      </div>

      {/* Categories */}
      <div className="flex-1 overflow-auto px-2 py-2 space-y-0.5">
        {filteredCategories.map(cat => (
          <PaletteCategory key={cat.id} category={cat} onDragStart={onDragStart} />
        ))}

        {filteredCategories.length === 0 && (
          <p className="text-center text-[11px] py-8" style={{ color: '#555' }}>
            No nodes match "{query}"
          </p>
        )}
      </div>

      {/* Help text */}
      <div className="px-3 py-2 border-t" style={{ borderColor: '#1A1A1A' }}>
        <p className="text-[9px]" style={{ color: '#333' }}>
          Drag nodes onto the canvas to build your agent workflow
        </p>
      </div>
    </div>
  );
}
